import { z } from 'zod';

import { ORDER_STATUSES } from '../domain';
import type { OrganizationType, UserRole } from '../domain';
import type { AgentEffect } from './effects';

/**
 * Whether the agent may carry an action out itself.
 *
 * Today every action is `propose`: the agent prepares it, the user presses the
 * button, and the request goes through the normal guarded API route. `confirm`
 * and `auto` are the seam for letting the agent act — flipping one action means
 * adding a server-side `run` handler and an `interrupt()` in the graph, with no
 * change here and none in the UI.
 */
export type AgentActionExecution = 'propose' | 'confirm' | 'auto';

export interface AgentActionDefinition {
  readonly describe: string;
  readonly schema: z.ZodType;
  readonly orgTypes: readonly OrganizationType[];
  readonly roles?: readonly UserRole[];
  readonly execution: AgentActionExecution;
  readonly effect: AgentEffect;
}

/** Identity, but it pins the literal types so AGENT_ACTIONS stays inferrable. */
function defineAction<const T extends AgentActionDefinition>(action: T): T {
  return action;
}

const uuid = () => z.uuid();

/**
 * Every action the agent may offer, declared once for both sides.
 *
 * The frontend imports this to execute an action; the API imports it to validate
 * what the model proposed and to describe the options in the prompt. There is no
 * manifest endpoint, so there is no version skew — and because the wire type is
 * `AgentActionKey`, an action the UI does not handle is a compile error rather
 * than a runtime null.
 */
// Frozen as well as `as const`: the readonly types are compile-time only, and
// this object is reachable from request-handling code, so nothing should be able
// to add an action at runtime. Shallow, matching what it replaces.
export const AGENT_ACTIONS = Object.freeze({
  open_part: defineAction({
    describe: 'Open a part detail page.',
    schema: z.object({ part_id: uuid() }).strict(),
    orgTypes: ['purchaser', 'supplier'],
    execution: 'propose',
    effect: { kind: 'navigate', path: '/parts/:part_id' },
  }),

  open_parts_list: defineAction({
    describe: 'Open the parts list, optionally pre-filtered by a search term.',
    schema: z.object({ search: z.string().max(120).optional() }).strict(),
    orgTypes: ['purchaser'],
    execution: 'propose',
    effect: { kind: 'navigate', path: '/parts', query: { q: ':search' } },
  }),

  import_parts: defineAction({
    describe: 'Open the part import flow.',
    schema: z.object({}).strict(),
    orgTypes: ['purchaser'],
    execution: 'propose',
    effect: { kind: 'navigate', path: '/parts/import' },
  }),

  open_rfq: defineAction({
    describe: 'Open an RFQ detail page.',
    schema: z.object({ rfq_id: uuid() }).strict(),
    orgTypes: ['purchaser', 'supplier'],
    execution: 'propose',
    effect: { kind: 'navigate', path: '/rfqs/:rfq_id' },
  }),

  open_rfqs_list: defineAction({
    describe: 'Open the RFQ list.',
    schema: z.object({}).strict(),
    orgTypes: ['purchaser', 'supplier'],
    execution: 'propose',
    effect: { kind: 'navigate', path: '/rfqs' },
  }),

  open_quote: defineAction({
    describe: 'Open a quote detail page.',
    schema: z.object({ quote_id: uuid() }).strict(),
    orgTypes: ['purchaser'],
    execution: 'propose',
    effect: { kind: 'navigate', path: '/quotes/:quote_id' },
  }),

  compare_quotes_for_rfq: defineAction({
    describe: "Open an RFQ's quote comparison view.",
    schema: z.object({ rfq_id: uuid() }).strict(),
    orgTypes: ['purchaser'],
    execution: 'propose',
    // The comparison lives on the RFQ detail page.
    effect: { kind: 'navigate', path: '/rfqs/:rfq_id' },
  }),

  open_order: defineAction({
    describe: 'Open an order detail page.',
    schema: z.object({ order_id: uuid() }).strict(),
    orgTypes: ['purchaser'],
    execution: 'propose',
    effect: { kind: 'navigate', path: '/orders/:order_id' },
  }),

  open_order_management: defineAction({
    describe: "Open an order's management page, to work on fulfilling it.",
    schema: z.object({ order_id: uuid() }).strict(),
    orgTypes: ['supplier'],
    execution: 'propose',
    effect: { kind: 'navigate', path: '/orders/management/:order_id' },
  }),

  open_orders_list: defineAction({
    describe: 'Open the orders list.',
    schema: z.object({ status: z.enum(ORDER_STATUSES).optional() }).strict(),
    orgTypes: ['purchaser', 'supplier'],
    execution: 'propose',
    effect: { kind: 'navigate', path: '/orders', query: { status: ':status' } },
  }),

  open_storefront: defineAction({
    describe: 'Open the storefront editor.',
    schema: z.object({}).strict(),
    orgTypes: ['supplier'],
    execution: 'propose',
    effect: { kind: 'navigate', path: '/storefront' },
  }),

  open_storefront_capabilities: defineAction({
    describe: 'Open the storefront capability picker, to add a missing capability.',
    schema: z.object({ highlight: z.string().max(80).optional() }).strict(),
    orgTypes: ['supplier'],
    execution: 'propose',
    // `highlight` is not in the URL today; the editor is the whole target.
    effect: { kind: 'navigate', path: '/storefront' },
  }),

  open_team_settings: defineAction({
    describe: 'Open team settings, e.g. to invite a colleague.',
    schema: z.object({}).strict(),
    orgTypes: ['purchaser', 'supplier'],
    execution: 'propose',
    effect: { kind: 'navigate', path: '/settings', query: { tab: 'team' } },
  }),
} as const satisfies Record<string, AgentActionDefinition>);

export type AgentActionKey = keyof typeof AGENT_ACTIONS;

export const AGENT_ACTION_KEYS = Object.keys(AGENT_ACTIONS) as AgentActionKey[];

export function isAgentActionKey(value: unknown): value is AgentActionKey {
  return typeof value === 'string' && Object.hasOwn(AGENT_ACTIONS, value);
}

/** The actions available to one organization type. */
export function agentActionKeysFor(organizationType: string): AgentActionKey[] {
  return AGENT_ACTION_KEYS.filter((key) =>
    (AGENT_ACTIONS[key].orgTypes as readonly string[]).includes(organizationType),
  );
}
